import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { PHONE, WHATSAPP_URL, BRAND, BRAND_LATIN } from "../lib/data";

const links = [
    { to: "/", label: "الرئيسية" },
    { to: "/services", label: "الخدمات" },
    { to: "/about", label: "من نحن" },
    { to: "/contact", label: "اتصل بنا" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-[rgba(10,10,10,0.85)] backdrop-blur-xl border-b border-[rgba(201,169,97,0.25)]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                    <div className="icon-orbit relative w-12 h-12 rounded-full flex items-center justify-center" style={{
                        background: "radial-gradient(circle at 30% 30%, rgba(255,248,214,0.15), rgba(10,10,10,0.6) 60%)",
                        boxShadow: "0 8px 20px -6px rgba(201,169,97,0.5)",
                    }}>
                        <Logo size={40} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display text-white text-xl">{BRAND}</div>
                        <div className="font-latin text-[10px] tracking-[0.35em] text-[#c9a961]">{BRAND_LATIN}</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => {
                        const active = location.pathname === l.to;
                        return (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                                className={`relative px-5 py-2 text-sm rounded-full transition-all ${
                                    active ? "text-[#0a0a0a] bg-gradient-to-l from-[#e8c86a] to-[#c9a961]" : "text-white/85 hover:text-[#e8c86a]"
                                }`}
                            >
                                {l.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href={`tel:${PHONE}`}
                        data-testid="nav-call-btn"
                        className="text-white/90 text-sm flex items-center gap-2 hover:text-[#e8c86a] transition"
                    >
                        <i className="fa-solid fa-phone" />
                        <span dir="ltr">{PHONE}</span>
                    </a>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-whatsapp-btn"
                        className="gold-btn text-sm"
                        style={{ padding: "0.6rem 1.4rem" }}
                    >
                        <i className="fa-brands fa-whatsapp ml-2" />واتساب
                    </a>
                </div>

                <button
                    className="lg:hidden text-white text-2xl"
                    onClick={() => setOpen(!open)}
                    data-testid="mobile-menu-toggle"
                    aria-label="menu"
                >
                    <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="lg:hidden bg-[#0a0a0a] border-t border-[rgba(201,169,97,0.2)] px-6 py-6"
                        data-testid="mobile-menu"
                    >
                        <div className="flex flex-col gap-4">
                            {links.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    className={`text-lg py-2 ${location.pathname === l.to ? "text-[#e8c86a]" : "text-white/85"}`}
                                    data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="gold-btn text-center mt-3" data-testid="mobile-whatsapp">
                                <i className="fa-brands fa-whatsapp ml-2" />تواصل عبر واتساب
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
